import { useMemo, useState, useSyncExternalStore } from "react";
import type { ReactNode, RefObject } from "react";

import {
  gridColumnFieldsSelector,
  gridColumnLookupSelector,
  gridColumnVisibilityModelSelector,
  useGridSelector,
  type GridApiPro,
} from "@mui/x-data-grid-pro";
import { ColumnsIcon, Button, MultiSelectDropdown } from "@stacklet/ui";

import type { MultiDropdownOption } from "@stacklet/ui";

interface Props {
  /** Grid API ref. Pass `useGridApiContext()` when inside a grid, or a ref from `useGridApiRef()` when outside. */
  apiRef: RefObject<GridApiPro>;
  /** Overrides the dropdown trigger; defaults to a labeled "Columns" button */
  trigger?: ReactNode;
}

/**
 * Tracks whether the grid's state has been initialized. The button may be
 * rendered as a sibling of the grid (e.g. in a custom toolbar row) and so
 * mount before the grid populates `apiRef.current.state` — reading grid
 * selectors during that window throws. We subscribe to the grid so the
 * dropdown mounts as soon as the grid is ready.
 */
function useGridStateReady(apiRef: RefObject<GridApiPro>) {
  return useSyncExternalStore(
    (onStoreChange) =>
      apiRef.current?.subscribeEvent?.("stateChange", onStoreChange) ??
      (() => {}),
    () => Boolean(apiRef.current?.state?.columns),
  );
}

function defaultTrigger() {
  return (
    <Button
      IconLeft={<ColumnsIcon size={20} />}
      aria-label="Columns"
      variant="tertiary"
    >
      Columns
    </Button>
  );
}

export function XGridColumnsButton({ apiRef, trigger }: Props) {
  const ready = useGridStateReady(apiRef);

  // Until the grid initializes, render the trigger only — reading column
  // selectors would throw. It becomes interactive once the grid is ready.
  if (!ready) {
    return trigger ?? defaultTrigger();
  }

  return <ColumnsDropdown apiRef={apiRef} trigger={trigger} />;
}

function ColumnsDropdown({ apiRef, trigger }: Props) {
  const columnFields = useGridSelector(apiRef, gridColumnFieldsSelector);
  const columnLookup = useGridSelector(apiRef, gridColumnLookupSelector);
  const visibilityModel = useGridSelector(
    apiRef,
    gridColumnVisibilityModelSelector,
  );
  const [columnSearchText, setColumnSearchText] = useState("");

  const columnOptions = useMemo<MultiDropdownOption[]>(
    () =>
      columnFields
        .map((field) => columnLookup[field])
        .filter((col) => col && col.hideable !== false)
        .map((col) => ({ id: col.field, name: col.headerName || col.field })),
    [columnFields, columnLookup],
  );

  const selectedColumns = useMemo(
    () => columnOptions.filter((col) => visibilityModel[col.id] !== false),
    [columnOptions, visibilityModel],
  );

  const filteredColumnOptions = useMemo(() => {
    const search = columnSearchText.toLowerCase();
    return search
      ? columnOptions.filter((col) =>
          String(col.name).toLowerCase().includes(search),
        )
      : columnOptions;
  }, [columnOptions, columnSearchText]);

  function setColumnVisibility(visibleIds: Set<string>) {
    apiRef.current.setColumnVisibilityModel(
      Object.fromEntries(
        columnOptions.map((col) => [col.id, visibleIds.has(col.id)]),
      ),
    );
  }

  function handleColumnChange(selected: MultiDropdownOption[]) {
    setColumnVisibility(new Set(selected.map((col) => col.id)));
  }

  function handleSelectAll() {
    setColumnVisibility(new Set(columnOptions.map((col) => col.id)));
    setColumnSearchText("");
  }

  function handleClearAll() {
    setColumnVisibility(new Set());
    setColumnSearchText("");
  }

  return (
    <MultiSelectDropdown
      label="Columns"
      onChange={handleColumnChange}
      onSearch={setColumnSearchText}
      options={filteredColumnOptions}
      placement="bottom right"
      searchValue={columnSearchText}
      topCTA={{
        left: { children: "Select All", onClick: handleSelectAll },
        right: { children: "Clear All", onClick: handleClearAll },
      }}
      trigger={trigger ?? defaultTrigger()}
      value={selectedColumns}
    />
  );
}
